import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  accountQuery,
  buildCasinoGameFilters,
  casinoGamesQuery,
  casinoVendorsQuery,
  useAuthSession,
} from '@/api';
import { CasinoFlag } from '@/domain';
import { BiggestWins } from './BiggestWins';
import { CasinoChrome } from './CasinoChrome';
import { GameCarousel } from './GameCarousel';
import { LatestWins } from './LatestWins';
import { ProvidersStrip } from './ProvidersStrip';
import { useCasinoFavorites } from './useCasinoFavorites';

export function CasinoLobby() {
  const navigate = useNavigate();
  const { user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const { showFavorite, toggleFavorite } = useCasinoFavorites();

  const onSearchCommit = useCallback(
    (value: string) => {
      if (!value) return;
      void navigate({
        to: '/casino/$category',
        params: { category: 'slots' },
        search: { q: value },
      });
    },
    [navigate],
  );

  const base = useMemo(
    () => buildCasinoGameFilters({ account, user, casino: true }),
    [account, user],
  );

  const hotFilters = useMemo(
    () => buildCasinoGameFilters({ account, user, popular: true, category: 'slots' }),
    [account, user],
  );
  const slotsFilters = useMemo(
    () => buildCasinoGameFilters({ account, user, category: 'slots' }),
    [account, user],
  );
  const liveFilters = useMemo(
    () => buildCasinoGameFilters({ account, user, liveMode: true }),
    [account, user],
  );
  const newFilters = useMemo(
    () =>
      buildCasinoGameFilters({
        account,
        user,
        category: 'slots',
        flagType: CasinoFlag.New,
      }),
    [account, user],
  );
  const bonusBuyFilters = useMemo(
    () =>
      buildCasinoGameFilters({
        account,
        user,
        category: 'slots',
        flagType: CasinoFlag.BonusBuy,
      }),
    [account, user],
  );
  const tableFilters = useMemo(
    () => buildCasinoGameFilters({ account, user, category: 'tablegames' }),
    [account, user],
  );
  const pokerFilters = useMemo(
    () => buildCasinoGameFilters({ account, user, category: 'poker' }),
    [account, user],
  );
  const gameShowFilters = useMemo(
    () => buildCasinoGameFilters({ account, user, category: 'gameshows' }),
    [account, user],
  );

  const hotQuery = useQuery(casinoGamesQuery(hotFilters));
  const slotsQuery = useQuery(casinoGamesQuery(slotsFilters));
  const liveQuery = useQuery(casinoGamesQuery(liveFilters));
  const newQuery = useQuery(casinoGamesQuery(newFilters));
  const bonusBuyQuery = useQuery(casinoGamesQuery(bonusBuyFilters));
  const tableQuery = useQuery(casinoGamesQuery(tableFilters));
  const pokerQuery = useQuery(casinoGamesQuery(pokerFilters));
  const gameShowQuery = useQuery(casinoGamesQuery(gameShowFilters));
  const vendorsQuery = useQuery(casinoVendorsQuery(base));

  return (
    <CasinoChrome activeNavId="lobby" showHero onSearchCommit={onSearchCommit}>
      <BiggestWins />
      <GameCarousel
        title="Hot Games"
        seeAllTo="/casino/hot"
        games={hotQuery.data?.Data ?? []}
        isLoading={hotQuery.isLoading}
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
      />
      <GameCarousel
        title="Top 10"
        seeAllTo="/casino/hot"
        games={(hotQuery.data?.Data ?? []).slice(0, 10)}
        isLoading={hotQuery.isLoading}
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
      />
      <GameCarousel
        title="Slots"
        seeAllTo="/casino/slots"
        games={slotsQuery.data?.Data ?? []}
        isLoading={slotsQuery.isLoading}
        categoryOverride="slots"
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
      />
      <GameCarousel
        title="Live Casino"
        seeAllTo="/casino/livecasino"
        games={liveQuery.data?.Data ?? []}
        isLoading={liveQuery.isLoading}
        categoryOverride="livecasino"
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
      />
      <LatestWins showSeeAll={false} />
      <GameCarousel
        title="Game Shows"
        seeAllTo="/casino/gameshows"
        games={gameShowQuery.data?.Data ?? []}
        isLoading={gameShowQuery.isLoading}
        categoryOverride="gameshows"
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
      />
      <GameCarousel
        title="New releases"
        seeAllTo="/casino/slots/newgames"
        games={newQuery.data?.Data ?? []}
        isLoading={newQuery.isLoading}
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
      />
      <ProvidersStrip vendors={vendorsQuery.data ?? []} category="slots" />
      <GameCarousel
        title="Feature Buy In"
        seeAllTo="/casino/slots/bonusbuy"
        games={bonusBuyQuery.data?.Data ?? []}
        isLoading={bonusBuyQuery.isLoading}
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
      />
      <GameCarousel
        title="Poker"
        seeAllTo="/casino/poker"
        games={pokerQuery.data?.Data ?? []}
        isLoading={pokerQuery.isLoading}
        categoryOverride="poker"
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
      />
      <GameCarousel
        title="Table Games"
        seeAllTo="/casino/tablegames"
        games={tableQuery.data?.Data ?? []}
        isLoading={tableQuery.isLoading}
        categoryOverride="tablegames"
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
      />
    </CasinoChrome>
  );
}
